import { useState } from "react";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import { CheckCircle2, XCircle, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { unsubscribeContactoDesdeEmail } from "@/lib/email-baja.functions";

const searchSchema = z.object({
  cid: z.string().uuid().optional(),
});

export const Route = createFileRoute("/email-baja")({
  validateSearch: (s) => searchSchema.parse(s),
  head: () => ({
    meta: [
      { title: "Dejar de recibir emails — Bizaclia" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: EmailBajaPage,
});

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto max-w-xl px-4 py-16">
      <Link
        to="/"
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Ir a Bizaclia
      </Link>
      <div className="rounded-2xl border border-border bg-background p-8 shadow-sm">
        {children}
      </div>
    </main>
  );
}

function EmailBajaPage() {
  const { cid } = useSearch({ from: "/email-baja" });
  const [state, setState] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [nombre, setNombre] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  if (!cid) {
    return (
      <Shell>
        <p className="text-muted-foreground">Enlace no válido.</p>
      </Shell>
    );
  }

  const onConfirm = async () => {
    setState("sending");
    setError(null);
    try {
      const res = await unsubscribeContactoDesdeEmail({ data: { contacto_id: cid } });
      setNombre(res.nombre);
      setEmail(res.email);
      setState("ok");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error");
      setState("error");
    }
  };

  if (state === "ok") {
    return (
      <Shell>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-8 w-8 text-primary" />
            <h1 className="text-2xl font-semibold">Baja confirmada</h1>
          </div>
          <p className="text-muted-foreground">
            {nombre ? <><strong>{nombre}</strong>, </> : null}
            hemos dado de baja {email ? <strong>{email}</strong> : "tu email"}. No volverás a
            recibir comunicaciones de Bizaclia.
          </p>
          <p className="text-sm text-muted-foreground">
            Si ha sido un error, escríbenos a{" "}
            <a href="mailto:hola@bizaclia.com" className="text-primary underline">
              hola@bizaclia.com
            </a>
            .
          </p>
        </div>
      </Shell>
    );
  }

  if (state === "error") {
    return (
      <Shell>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <XCircle className="h-8 w-8 text-destructive" />
            <h1 className="text-2xl font-semibold">No se pudo procesar</h1>
          </div>
          <p className="text-muted-foreground">{error}</p>
          <Button onClick={onConfirm} variant="outline">Reintentar</Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">¿Dejar de recibir emails?</h1>
        <p className="text-muted-foreground">
          Si confirmas, no volveremos a enviarte más correos desde Bizaclia
          (avisos de coincidencias, novedades ni notificaciones).
        </p>
        <Button
          onClick={onConfirm}
          disabled={state === "sending"}
          variant="destructive"
          className="w-full"
        >
          {state === "sending" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Procesando…
            </>
          ) : (
            "Sí, darme de baja"
          )}
        </Button>
        <p className="text-xs text-muted-foreground text-center">
          Podrás volver a activarlos desde tu perfil cuando quieras.
        </p>
      </div>
    </Shell>
  );
}
